<script setup lang="ts">
import { computed } from "vue";
import {
  HLButton,
  HLIcon,
  HLModal,
  HLProgressSteps,
  HLSpace,
  HLText,
} from "@platform-ui/highrise";
import {
  AlertCircleIcon,
  CheckCircleBrokenIcon,
  CheckDone01Icon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ClipboardCheckIcon,
  PlayCircleIcon,
  Save01Icon,
} from "@gohighlevel/ghl-icons/24/outline";
import { MaterialBugReportIcon } from "@gohighlevel/ghl-icons/24/material/rounded";

type TicketMode = "create" | "edit";
type TicketType = "bug" | "testRun" | "testCase" | "feature";

const props = withDefaults(
  defineProps<{
    id?: string;
    show: boolean;
    title?: string;
    subtitle?: string;
    modalWidth?: number;
    showClose?: boolean;
    maskClosable?: boolean;
    mode?: TicketMode;
    ticketType: TicketType;
    step?: number;
    allowStepNavigation?: boolean;
    showDefaultActions?: boolean;
    saving?: boolean;
    disableNext?: boolean;
    disableSubmit?: boolean;
    backText?: string;
    nextText?: string;
    submitText?: string;
  }>(),
  {
    id: "ticket-create-edit-steps",
    title: "",
    subtitle: "",
    modalWidth: 600,
    showClose: false,
    maskClosable: false,
    mode: "create",
    step: 0,
    allowStepNavigation: true,
    showDefaultActions: true,
    saving: false,
    disableNext: false,
    disableSubmit: false,
    backText: "Previous",
    nextText: "Next",
    submitText: "Save",
  },
);

const emit = defineEmits<{
  (event: "update:show", value: boolean): void;
  (event: "close"): void;
  (event: "update:step", value: number): void;
  (event: "previous"): void;
  (event: "next"): void;
  (event: "submit"): void;
}>();

const clampedStep = computed(() => Math.min(2, Math.max(0, props.step)));
const isFirstStep = computed(() => clampedStep.value === 0);
const isLastStep = computed(() => clampedStep.value === 2);

const ticketLabel = computed(() =>
  props.ticketType
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" "),
);

const actionLabel = computed(() => (props.mode === "edit" ? "Edit" : "Create"));

const steps = computed(() =>
  [
    {
      title: "Properties",
      subtitle1: `Core ${ticketLabel.value.toLowerCase()} settings and status details.`,
    },
    {
      title: "Details",
      subtitle1: `Primary ${ticketLabel.value.toLowerCase()} description and metadata.`,
    },
    {
      title: "Attachments",
      subtitle1: `Connect related test cases, runs, bugs, and features.`,
    },
  ].map((stepItem, index) => ({
    id: `${props.id}-step-${index + 1}`,
    ...stepItem,
    status:
      index === clampedStep.value
        ? ("current" as const)
        : index < clampedStep.value
          ? ("complete" as const)
          : ("default" as const),
    className: props.allowStepNavigation ? "clickable-step" : undefined,
  })),
);

const updateStep = (nextStep: number) => {
  emit("update:step", Math.min(2, Math.max(0, nextStep)));
};

const goPrevious = () => {
  if (isFirstStep.value) {
    return;
  }
  updateStep(clampedStep.value - 1);
  emit("previous");
};

const goNext = () => {
  if (isLastStep.value) {
    emit("submit");
    return;
  }
  updateStep(clampedStep.value + 1);
  emit("next");
};

const handleStepClick = (index: number) => {
  if (!props.allowStepNavigation) {
    return;
  }
  updateStep(index);
};

const handleShowUpdate = (value: boolean) => {
  emit("update:show", value);
  if (!value) {
    emit("close");
  }
};

const IconMap: Record<TicketType, any> = {
  bug: MaterialBugReportIcon,
  feature: CheckDone01Icon,
  testRun: PlayCircleIcon,
  testCase: ClipboardCheckIcon,
};
</script>

<template>
  <HLModal
    :id="`${props.id}-modal`"
    :show="props.show"
    :show-header="true"
    :show-footer="true"
    :show-close="true"
    :mask-closable="props.maskClosable"
    :close-on-esc="true"
    :width="props.modalWidth"
    class="ticket-create-edit-stepper-modal"
    :show-header-icon="false"
    @update:show="handleShowUpdate"
  >
    <template #header>
      <slot
        name="header"
        :mode="props.mode"
        :ticket-type="props.ticketType"
        :ticket-label="ticketLabel"
        :action-label="actionLabel"
      >
        <div class="flex items-center gap-3 px-3 pt-2 pb-3">
          <div class="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-primary-50 dark:bg-primary-900/30">
            <HLIcon
              size="20"
              class="text-primary-600 dark:text-primary-400"
            >
              <component :is="IconMap[props.ticketType]" />
            </HLIcon>
          </div>
          <div>
            <HLText
              size="2xl"
              weight="semibold"
              html-tag="span"
              class="block"
            >
              {{ props.title || `${actionLabel} ${ticketLabel}` }}
            </HLText>
            <HLText
              v-if="props.subtitle"
              size="sm"
              color="muted"
              class="block"
            >
              {{ props.subtitle }}
            </HLText>
          </div>
        </div>
      </slot>
    </template>

    <div class="px-1 pb-8">
      <HLSpace
        vertical
        :size="16"
      >
        <div class="py-2">
          <HLProgressSteps
            :id="props.id"
            :steps="steps"
            centered
          />
        </div>

        <div class="ticket-step-content">
          <slot
            v-if="clampedStep === 0"
            name="ticket-properties"
            :mode="props.mode"
            :ticket-type="props.ticketType"
            :ticket-label="ticketLabel"
          />
          <slot
            v-else-if="clampedStep === 1"
            name="ticket-info"
            :mode="props.mode"
            :ticket-type="props.ticketType"
            :ticket-label="ticketLabel"
          />
          <slot
            v-else
            name="links"
            :mode="props.mode"
            :ticket-type="props.ticketType"
            :ticket-label="ticketLabel"
          />
        </div>
      </HLSpace>
    </div>
    <template #footer>
      <slot
        name="actions"
        :step="clampedStep"
        :is-first-step="isFirstStep"
        :is-last-step="isLastStep"
        :go-next="goNext"
        :go-previous="goPrevious"
        :mode="props.mode"
        :ticket-type="props.ticketType"
      >
        <div
          v-if="props.showDefaultActions"
          class="modal-footer justify-between"
        >
          <HLButton
            size="xs"
            variant="secondary"
            color="primary"
            :disabled="isFirstStep"
            @click="goPrevious"
          >
            <template #iconLeft>
              <ChevronLeftIcon />
            </template>
            {{ props.backText }}
          </HLButton>
          <div class="flex gap-2">
            <HLButton
              size="xs"
              variant="secondary"
              color="gray"
              @click="handleShowUpdate(false)"
            >
              Cancel
            </HLButton>
            <HLButton
              size="xs"
              variant="primary"
              color="primary"
              :loading="props.saving"
              :disabled="isLastStep ? props.disableSubmit : props.disableNext"
              @click="goNext"
            >
              <template
                v-if="!isLastStep"
                #iconRight
              >
                <ChevronRightIcon />
              </template>
              <template
                v-if="isLastStep"
                #iconLeft
              >
                <CheckCircleBrokenIcon />
              </template>
              {{ isLastStep ? props.submitText : props.nextText }}
            </HLButton>
          </div>
        </div>
      </slot>
    </template>
  </HLModal>
</template>

<style scoped>
.ticket-step-content {
  min-height: 160px;
  max-height: 420px;
  overflow-y: auto;
}

.clickable-step {
  cursor: pointer;
}

.clickable-step:hover {
  opacity: 0.9;
}

:deep(.hr-progress-step__icon path) {
  fill: var(--primary-600);
}
</style>
